import React, { useState } from "react";
import { useLoader } from "../../Common/GeneralLoader/GeneralLoaderProvider";
import { getRecipientDetailsApi, getRecipientListApi } from "../../../api/api";
import { drupalSettings } from "../../../utils/config";

export const SelectRecipient = ({ setStep, payload, setPayload }) => {

  const [error, setError] = useState(false);
  const { showLoader, hideLoader } = useLoader(); // Destructure isLoading, showLoader, and hideLoader

  const formErrors = {
    recipient_required: Drupal.t("Recipient field is required."),
    technical_error:
      Drupal.t("Sorry, there was an error. Please try again later or reach out to support@nautzero.com."),
  };

  // State for form inputs
  const [recipientList, setRecipientList] = useState([]);
  const [recipient, setRecipient] = useState("");
  const selectRef = React.useRef(null);

  const handleSubmit = (event) => {
    event.preventDefault();

    let isValid = true; // Flag to track if all validations pass

    // Validate Card ID
    if (!recipient) {
      setError(formErrors?.recipient_required);
      isValid = false;
    }

    // If any validation failed, prevent form submission
    if (!isValid) {
      return;
    }

    setError(false);

    (async () => {
      try {
        showLoader();
        // Fetch recipient details or perform any other action
        const response = await getRecipientDetailsApi({ yatchId: drupalSettings?.user?.yacht_id, recipientId: recipient });
        setPayload({ ...payload, recipientId: recipient, recipientDetails: response }); // Update payload with the selected recipient details and ID

        // Move to the next step
        setStep(3.1);

      } catch (err) {
        console.error("Failed to fetch recipient details:", err);
        setError(formErrors?.technical_error);
      } finally {
        hideLoader();
      }
    })();
  };

  // Function to fetch user info
  const fetchRecipientList = async () => {
    showLoader();
    try {
      const response = await getRecipientListApi({ yatchId: drupalSettings?.user?.yacht_id });
      const list = response || [];
      const sorted = [...list].sort((a, b) => {
        const nameA = (a.title || "").toString().trim().toLowerCase();
        const nameB = (b.title || "").toString().trim().toLowerCase();
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      });
      setRecipientList(sorted);
    } catch (err) {
      console.log("Failed to fetch locations info");
    } finally {
      hideLoader();
    }
  };

  // Fetch user info when the component mounts
  React.useEffect(() => {
    fetchRecipientList();
  }, []);

  React.useEffect(() => {
    const $select = jQuery(selectRef.current);
    if (!$select.length) return undefined;

    if ($select.hasClass("select2-hidden-accessible")) {
      $select.select2("destroy");
    }

    const renderRecipientOption = (data) => {
      if (!data.id) {
        return data.text;
      }
      const $el = jQuery(data.element);
      const country = $el.data("country") || Drupal.t("N/A");
      const currency = $el.data("currency") || Drupal.t("N/A");
      const clearing = $el.data("clearing") || "";
      const account = $el.data("account") || "";
      const normalizedClearing = String(clearing || "").trim();
      const normalizedAccount = String(account || "").trim();
      const details = [
        `${Drupal.t("Country")}: ${country}`,
        `${Drupal.t("Currency")}: ${currency}`,
      ];
      if (normalizedAccount) {
        details.push(`${Drupal.t("Account")}: ${normalizedAccount}`);
      }
      if (normalizedClearing && normalizedAccount !== normalizedClearing) {
        details.push(`${Drupal.t("Clearing")}: ${normalizedClearing}`);
      }
      const searchText = [data.text, country, currency, normalizedClearing, normalizedAccount]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const text = `
        <div class="recipient-option" style="padding:6px 0;" data-search-text="${searchText.replace(/"/g, "&quot;")}">
          <div class="recipient-option__title" style="font-weight:700;line-height:1.25;word-break:break-word;">${data.text || ""}</div>
          <div class="recipient-option__meta" style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px;">
            ${details.map((item) => `<span class="recipient-option__chip" style="display:inline-block;background:#f6f7f9;border:1px solid #e7e8ea;border-radius:4px;padding:2px 6px;font-size:12px;line-height:1.25;max-width:100%;word-break:break-word;">${item}</span>`).join("")}
          </div>
        </div>
      `;
      return jQuery(text);
    };

    $select.select2({
      dropdownParent: jQuery("body"),
      width: "100%",
      placeholder: Drupal.t("Search stored recipients"),
      minimumResultsForSearch: 0,
      matcher: (params, data) => {
        const term = (params?.term || "").trim().toLowerCase();
        if (!term) {
          return data;
        }
        if (!data.element) {
          return null;
        }
        const $el = jQuery(data.element);
        const haystack = [
          data.text,
          $el.data("country"),
          $el.data("currency"),
          $el.data("clearing"),
          $el.data("account"),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term) ? data : null;
      },
      templateResult: renderRecipientOption,
      templateSelection: (data) => data.text || Drupal.t("Select"),
      escapeMarkup: (markup) => markup,
    });
    const select2Instance = $select.data("select2");
    if (select2Instance?.$container) {
      select2Instance.$container.addClass("recipient-select2");
    }
    if (select2Instance?.$dropdown) {
      select2Instance.$dropdown.addClass("recipient-select2-dropdown");
    }

    const onChange = function () {
      const value = jQuery(this).val();
      if (jQuery(this).attr("name") === "recipient") {
        setRecipient(value);
      }
    };

    $select.on("change", onChange);
    return () => {
      $select.off("change", onChange);
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.select2("destroy");
      }
    };
  }, [recipientList]);

  React.useEffect(() => {
    console.log("recipientList or payload changed", recipientList, payload);
    if (payload?.recipientId) {
      jQuery('.select-recipient-form .select2')
        .val(payload.recipientId)
        .trigger('change');
    }
  }, [recipientList, payload]);

  return (
    <>
      <div className="create-transfer-header">
        <h4 className="create-transfer-title"><span className="yellow">Select</span> Recipient</h4>
      </div>
      <div className="create-transfer-body-content">
        <div className="create-transfer-description">
          Please select which stored recipient you would to create a transfer for.
        </div>
        <form className="select-recipient-form">
          <div className="form--item form--group">
            <label className="form--label">Recipient</label>
            <select ref={selectRef} className="form-select select2" name="recipient" data-custom-select2="true">
              <option value="">Select</option>
              {recipientList.map((recipient) => (
                <option
                  key={recipient.id}
                  value={recipient.id}
                  data-country={recipient?.details?.country || ""}
                  data-currency={recipient?.details?.account_currency || ""}
                  data-clearing={recipient?.details?.clearing_routing_number || recipient?.details?.ach_number || recipient?.details?.iban_number || ""}
                  data-account={recipient?.details?.account_number || recipient?.details?.iban_number || recipient?.details?.ach_number || ""}
                >
                  {recipient.title}
                </option>
              ))}
            </select>
            {error && (
              <div className="popup-has-error">
                <div className="error_messages">{error}</div>
              </div>
            )}
          </div>
        </form>
        <div className="create-transfer-actions">
          <button className="secondary__btn" type="submit" onClick={() => setStep(1.1)}>Go Back</button>
          <button className="primary__btn" type="submit" onClick={handleSubmit}>Next</button>
        </div>
      </div>
    </>
  );
};
